// apps/web/src/pages/brand/Ideas.tsx
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DragDropContext, type DropResult } from "@hello-pangea/dnd";
import { useTranslation } from "react-i18next";
import { useBrandContext } from "@/contexts/brand-context";
import { usePermissions } from "@/hooks/use-permissions";
import { useIdeas, useCreateIdea, useUpdateIdea, useDeleteIdea, useConvertIdea, type Idea, type IdeaStatus } from "@/hooks/use-ideas";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { IdeaColumn } from "@/components/ideas/IdeaColumn";
import { IdeaEditDrawer } from "@/components/ideas/IdeaEditDrawer";
import { GenerateIdeasEmpty } from "@/components/ideas/generate-ideas-empty";
import { EmptyState } from "@/components/ui/empty-state";
import { Lightbulb } from "lucide-react";
import { toast } from "sonner";

const COLUMNS: { status: IdeaStatus; disableDrop?: boolean }[] = [
  { status: "BACKLOG" },
  { status: "SELECTED" },
  { status: "CONVERTED", disableDrop: true },
];

export function IdeasPage() {
  const { t } = useTranslation("ideas");
  const { brand } = useBrandContext();
  const { canManageBrand } = usePermissions();
  const brandId = brand?.id ?? "";
  const navigate = useNavigate();

  const { data: ideas, isLoading } = useIdeas(brandId);
  const createIdea = useCreateIdea(brandId);
  const updateIdea = useUpdateIdea(brandId);
  const deleteIdea = useDeleteIdea(brandId);
  const convertIdea = useConvertIdea(brandId);

  const [editing, setEditing] = useState<Partial<Idea> | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const grouped = useMemo(() => {
    const groups: Record<IdeaStatus, Idea[]> = { BACKLOG: [], SELECTED: [], CONVERTED: [] };
    for (const i of ideas ?? []) groups[i.status].push(i);
    return groups;
  }, [ideas]);

  if (isLoading) {
    return (
      <div className="px-6 py-8 w-full space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  async function onDragEnd(result: DropResult) {
    if (!result.destination) return;
    if (result.source.droppableId === result.destination.droppableId) return;
    const ideaId = result.draggableId;
    const newStatus = result.destination.droppableId as IdeaStatus;
    if (newStatus === "CONVERTED") return; // drop disabled, but guard
    try {
      await updateIdea.mutateAsync({ id: ideaId, patch: { status: newStatus } });
    } catch (e: any) {
      toast.error(e.message ?? t("ideas:page.toast.moveFailed"));
    }
  }

  function openEdit(idea: Idea) {
    setEditing(idea);
    setDrawerOpen(true);
  }
  function openCreate() {
    setEditing(null);
    setDrawerOpen(true);
  }
  async function handleSubmit(input: Partial<Idea>) {
    if (editing?.id) {
      await updateIdea.mutateAsync({ id: editing.id, patch: input });
      toast.success(t("ideas:page.toast.updated"));
    } else {
      await createIdea.mutateAsync(input);
      toast.success(t("ideas:page.toast.created"));
    }
  }
  async function handleDelete(idea: Idea) {
    if (!confirm(t("ideas:page.confirm.delete", { title: idea.title }))) return;
    await deleteIdea.mutateAsync(idea.id);
    toast.success(t("ideas:page.toast.deleted"));
  }
  async function handlePromote(idea: Idea) {
    if (!confirm(t("ideas:page.confirm.promote", { title: idea.title }))) return;
    const { projectId } = await convertIdea.mutateAsync(idea.id);
    toast.success(t("ideas:page.toast.promoted"));
    navigate(`/${brand?.slug}/projects/${projectId}`);
  }

  return (
    <div className="px-6 py-8 w-full space-y-6 flex flex-col h-full">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t("ideas:page.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("ideas:page.subtitle")}</p>
        </div>
        {canManageBrand && <Button onClick={openCreate}>{t("ideas:page.newIdea")}</Button>}
      </header>

      {(ideas?.length ?? 0) === 0 ? (
        canManageBrand ? (
          <GenerateIdeasEmpty brandId={brandId} />
        ) : (
          <EmptyState
            icon={Lightbulb}
            title={t("ideas:page.emptyTitle")}
            description={t("ideas:page.emptyDescription")}
          />
        )
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 flex-1 min-h-0">
            {COLUMNS.map((col) => (
              <IdeaColumn
                key={col.status}
                status={col.status}
                title={t(`ideas:columns.${col.status}`)}
                ideas={grouped[col.status]}
                disableDrop={col.disableDrop}
                canEdit={canManageBrand}
                onCardClick={openEdit}
                onEdit={openEdit}
                onDelete={handleDelete}
                onPromote={handlePromote}
              />
            ))}
          </div>
        </DragDropContext>
      )}

      <IdeaEditDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        initialIdea={editing}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
